import React, { useState, useEffect } from 'react';
import { membersAPI } from '../lib/api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { User, Mail, Phone, MapPin, Calendar, Church, Loader2 } from 'lucide-react';

const statusMap = {
  approved: { label: 'Aprobado', color: 'bg-green-100 text-green-700' },
  pending: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-700' },
  rejected: { label: 'Rechazado', color: 'bg-red-100 text-red-700' },
};

const genderMap = { male: 'Masculino', female: 'Femenino' };

function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return (
    <div className="flex items-start gap-3 py-2">
      <div className="w-8 h-8 rounded-lg bg-[#056A84]/8 flex items-center justify-center shrink-0 mt-0.5">
        <Icon className="w-4 h-4 text-[#056A84]" />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</p>
        <p className="text-sm font-medium text-foreground break-words">{text}</p>
      </div>
    </div>
  );
}

export default function MemberProfileDialog({ memberId, open, onClose }) {
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && memberId) {
      setLoading(true);
      membersAPI.get(memberId)
        .then(res => setMember(res.data?.data || null))
        .catch(() => setMember(null))
        .finally(() => setLoading(false));
    }
  }, [open, memberId]);

  const m = member || {};
  const si = statusMap[m.church_approval_status] || statusMap.pending;
  const fullName = `${m.firstname || ''} ${m.lastname || ''}`.trim() || 'Sin nombre';
  const formatDate = (value) => value
    ? new Date(value).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] p-0" data-testid="member-profile-dialog">
        <DialogHeader className="sr-only">
          <DialogTitle>Perfil del miembro</DialogTitle>
          <DialogDescription>Detalle del miembro de la congregación</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 spinner text-[#056A84]" />
          </div>
        ) : !member ? (
          <div className="p-6 text-center text-muted-foreground">No se encontró el miembro</div>
        ) : (
          <ScrollArea className="max-h-[85vh]">
            <div className="bg-gradient-to-br from-[#062B36] to-[#056A84] p-6 text-white">
              <div className="flex items-center gap-4">
                {m.avatar_url ? (
                  <img src={m.avatar_url} alt={fullName} className="w-16 h-16 rounded-full border-2 border-white/20 object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-2xl font-bold">
                    {(m.firstname || 'U')[0]}{(m.lastname || '')[0]}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-semibold truncate">{fullName}</h2>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${si.color}`}>
                      {si.label}
                    </span>
                    {m.role && (
                      <Badge variant="secondary" className="text-[10px] bg-white/20 text-white border-0 capitalize">
                        {typeof m.role === 'object' ? (m.role.name || 'member') : m.role}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Información personal</h3>
                <InfoRow icon={Mail} label="Correo" value={m.email} />
                <InfoRow icon={Phone} label="Teléfono" value={m.phone} />
                <InfoRow icon={MapPin} label="Dirección" value={m.address} />
                <InfoRow icon={Calendar} label="Fecha de nacimiento" value={formatDate(m.birthdate)} />
                <InfoRow icon={User} label="Género" value={genderMap[m.gender] || m.gender} />
              </div>

              <Separator />

              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Congregación</h3>
                <InfoRow icon={Church} label="Iglesia" value={m.church_name || m.church?.name} />
                <InfoRow icon={Calendar} label="Miembro desde" value={formatDate(m.created_at)} />
              </div>

              {(m.permissions || []).length > 0 && (
                <>
                  <Separator />
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Permisos de equipo</h3>
                    <div className="flex flex-wrap gap-2">
                      {(m.permissions || []).map(p => (
                        <Badge key={typeof p === 'object' ? p.key || JSON.stringify(p) : p} variant="secondary" className="text-xs">
                          {typeof p === 'object' ? (p.label || p.key || JSON.stringify(p)) : p}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <Separator />
              <p className="text-xs text-muted-foreground">ID: {m.id}</p>
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
